import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { WsSessionReaction } from '../../services/socket.service';

const WINDOW_MS = 30_000; // 30-second rolling window

interface ReactionEntry {
  emoji: string;
  timestamp: number;
}

interface ReactionFeedProps {
  /** Pass reactions from the parent's useSessionSocket onSessionReaction callback */
  reactions: WsSessionReaction[];
  /** Compact mode for PiP widget — tighter spacing, smaller text */
  compact?: boolean;
  /** Dark background mode for PiP widget — dark pills with white text */
  darkBackground?: boolean;
}

const ReactionFeed: React.FC<ReactionFeedProps> = ({ reactions, compact, darkBackground }) => {
  const [entries, setEntries] = useState<ReactionEntry[]>([]);
  const cleanupRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Add incoming reactions to the rolling window
  useEffect(() => {
    if (reactions.length === 0) return;
    const latest = reactions[reactions.length - 1];
    setEntries((prev) => [
      ...prev,
      { emoji: latest.emoji, timestamp: Date.now() },
    ]);
  }, [reactions]);

  // Clean up expired entries every 5 seconds
  useEffect(() => {
    cleanupRef.current = setInterval(() => {
      const cutoff = Date.now() - WINDOW_MS;
      setEntries((prev) => prev.filter((e) => e.timestamp > cutoff));
    }, 5000);
    return () => {
      if (cleanupRef.current) clearInterval(cleanupRef.current);
    };
  }, []);

  // Group by emoji and count
  const grouped = entries.reduce<Record<string, number>>((acc, entry) => {
    const cutoff = Date.now() - WINDOW_MS;
    if (entry.timestamp > cutoff) {
      acc[entry.emoji] = (acc[entry.emoji] || 0) + 1;
    }
    return acc;
  }, {});

  const emojiCounts = Object.entries(grouped).sort((a, b) => b[1] - a[1]);

  if (emojiCounts.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0.75 : 1,
        px: compact ? 1 : 1.5,
        py: compact ? 0.25 : 0.5,
        bgcolor: darkBackground ? 'rgba(35,35,66,0.9)' : 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(6px)',
        borderRadius: compact ? '12px' : '16px',
        border: '1px solid',
        borderColor: darkBackground ? 'rgba(255,255,255,0.06)' : 'divider',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      {emojiCounts.map(([emoji, count]) => {
        const isRaiseHand = emoji === '\u{270B}';
        return (
          <Box
            key={emoji}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              px: compact ? 0.5 : 0.75,
              py: 0.25,
              borderRadius: '12px',
              bgcolor: isRaiseHand
                ? (darkBackground ? 'rgba(255,152,0,0.2)' : 'rgba(255,152,0,0.12)')
                : 'transparent',
              animation: isRaiseHand ? 'raiseHandPulse 2s ease-in-out infinite' : undefined,
              '@keyframes raiseHandPulse': {
                '0%, 100%': { bgcolor: 'rgba(255,152,0,0.12)' },
                '50%': { bgcolor: 'rgba(255,152,0,0.25)' },
              },
              position: 'relative',
            }}
          >
            <Typography sx={{ fontSize: compact ? 14 : 16, lineHeight: 1 }}>{emoji}</Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: compact ? 11 : 12,
                color: isRaiseHand
                  ? (darkBackground ? '#fbbf24' : 'warning.dark')
                  : (darkBackground ? 'rgba(255,255,255,0.7)' : 'text.secondary'),
                minWidth: 12,
                textAlign: 'center',
                ...(isRaiseHand && {
                  animation: 'raiseHandTextPulse 2s ease-in-out infinite',
                  '@keyframes raiseHandTextPulse': {
                    '0%, 100%': { opacity: 0.7 },
                    '50%': { opacity: 1 },
                  },
                }),
              }}
            >
              {count}
            </Typography>
            {/* Orange dot badge on raise hand */}
            {isRaiseHand && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#f97316',
                  border: '1px solid #fff',
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/** Hook to collect reactions from WebSocket into an array for ReactionFeed */
export function useReactionCollector() {
  const [reactions, setReactions] = useState<WsSessionReaction[]>([]);

  const addReaction = useCallback((data: WsSessionReaction) => {
    setReactions((prev) => [...prev, data]);
  }, []);

  return { reactions, addReaction };
}

export default ReactionFeed;
